import React, { useEffect, useState } from 'react';
import { FaPlus, FaCheck, FaPencilAlt, FaTrash, FaTimes, FaArrowLeft, FaArrowRight, FaFileAlt, FaEdit, FaListUl, FaSave, FaInfoCircle } from 'react-icons/fa';
import Button from '../../components/buttons/Button';
import { enterpriseAdminService } from '../../services';
import Toggle from '../../components/Toggle/Toggle';
import FullScreenTextEditor from '../../components/editors/FullScreenTextEditor';
import ReportTypeCard from '../../components/ReportType/ReportTypeCard';
import { useLoading } from '../../context/LoadingContext';
import useNotification from '../../components/Notifications/useNotifications';
import Notification from '../../components/Notifications/Notification';
import './ReportTypesTab.css';
import '../../Templates/TemplateBuilder/TemplateBuilderWizard.css';
import CreateEnterpriseReportTypeWizard from './CreateEnterpriseReportTypeWizard';
import { formatDisplayName } from '../../models/ReportTypeDesignHelper';
import { createLogger } from '../../services/Debug';

function ReportTypesTab({ enterpriseId, enterpriseShortName }) {
  const log = createLogger('ReportTypesTab');
  const { setIsLoading } = useLoading();
  const { notification, showNotification, hideNotification } = useNotification();

  // Report types state
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(false);

  // Favorites state (active types)
  const [activeTypes, setActiveTypes] = useState([]);
  const [inactiveTypes, setInactiveTypes] = useState([]);


  // Creation overlay state
  const [isCreating, setIsCreating] = useState(false);
  const [createStep, setCreateStep] = useState(1);
  const [newName, setNewName] = useState('');
  const [newCategory, setNewCategory] = useState('rapport_long');
  const [newDescription, setNewDescription] = useState('');
  const [newInstructions, setNewInstructions] = useState('');
  const [customEnterpriseInstructions, setCustomEnterpriseInstructions] = useState('');
  const [exampleProportion, setExampleProportion] = useState(75);
  const [creating, setCreating] = useState(false);
  const [nameTouched, setNameTouched] = useState(false);

  // Edit modal state
  const [editing, setEditing] = useState(null);
  const [editedProportion, setEditedProportion] = useState(75);
  const [editedIsFavorite, setEditedIsFavorite] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editingExamples, setEditingExamples] = useState([]);
  const [examplesLoading, setExamplesLoading] = useState(false);
  const [newExampleTranscription, setNewExampleTranscription] = useState('');
  const [newExampleContent, setNewExampleContent] = useState('');
  const [customInstructionsDraft, setCustomInstructionsDraft] = useState('');
  const [showInstructionEditor, setShowInstructionEditor] = useState(false);
  const [showExampleEditor, setShowExampleEditor] = useState(false);
  const [showExamplesManager, setShowExamplesManager] = useState(false);
  const [showTranscriptionEditor, setShowTranscriptionEditor] = useState(false);
  const [searchExamples, setSearchExamples] = useState('');
  const [editingExampleId, setEditingExampleId] = useState(null);
  const [savingExample, setSavingExample] = useState(false);
  const [expandedExampleId, setExpandedExampleId] = useState(null);

  // Track changes for save/cancel
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (!enterpriseId) return;
    setIsLoading({ isLoading: true, text: 'Chargement des modèles organisation...' });
    (async () => {
      try {
        await loadTypes();
      } catch (err) {
        log.error('Failed to load enterprise report types', err);
      } finally {
        setIsLoading({ isLoading: false });
      }
    })();
  }, [enterpriseId]);

  const loadTypes = async () => {
    const enterpriseTypes = await enterpriseAdminService.getEnterpriseReportTypes(enterpriseId);
    setTypes(enterpriseTypes);
    
    // Separate active and inactive types
    const active = enterpriseTypes.filter(t => t.isFavorite).sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0));
    const inactive = enterpriseTypes.filter(t => !t.isFavorite);
    
    setActiveTypes(active);
    setInactiveTypes(inactive);
  };

  // Toggle active/inactive status
  const handleToggleFavorite = async (type) => {
    const isFavorite = activeTypes.some(t => t.id === type.id);
    
    if (isFavorite) {
      // Move from active to inactive
      setActiveTypes(prev => prev.filter(t => t.id !== type.id));
      setInactiveTypes(prev => [...prev, { ...type, isFavorite: false }]);
    } else {
      // Move from inactive to active
      setInactiveTypes(prev => prev.filter(t => t.id !== type.id));
      setActiveTypes(prev => [...prev, { ...type, isFavorite: true }]);
    }
    
    setHasChanges(true);
  };


  // Save changes
  const handleSaveChanges = async () => {
    setSavingEdit(true);
    try {
      // Update each type with new favorite status and order
      const promises = [];
      
      // Update active types
      activeTypes.forEach((type, index) => {
        promises.push(
          enterpriseAdminService.updateEnterpriseReportType(enterpriseId, type.id, {
            isFavorite: true,
            orderIndex: index
          })
        );
      });
      
      // Update inactive types
      inactiveTypes.forEach(type => {
        promises.push(
          enterpriseAdminService.updateEnterpriseReportType(enterpriseId, type.id, {
            isFavorite: false,
            orderIndex: null
          })
        );
      });
      
      await Promise.all(promises);
      await loadTypes();
      setHasChanges(false);
      showNotification('Modifications sauvegardées avec succès', 'success');
    } catch (error) {
      log.error('Error saving changes:', error);
      showNotification('Erreur lors de la sauvegarde', 'error');
    } finally {
      setSavingEdit(false);
    }
  };

  // Cancel changes
  const handleCancelChanges = () => {
    loadTypes();
    setHasChanges(false);
  };

  // Creation helpers
  const validateName = (name) => {
    if (!name || name.length > 50) return false;
    return /^[a-zA-Z\s]+$/.test(name);
  };

  const openCreate = () => {
    setIsCreating(true);
    setCreateStep(1);
    setNewName('');
    setNewCategory('rapport_long');
    setNewDescription('');
    setCustomEnterpriseInstructions('');
    setExampleProportion(75);
    setNameTouched(false);
  };

  const cancelCreate = () => {
    setIsCreating(false);
    setCreateStep(1);
    setNewName('');
    setNewDescription('');
    setCustomEnterpriseInstructions('');
    setExampleProportion(75);
  };

  const saveCreate = async () => {
    if (!validateName(newName)) {
      alert('Le nom doit contenir uniquement des lettres (max 50)');
      return;
    }
    if (!enterpriseId) {
      alert("Aucune organisation sélectionnée");
      return;
    }
    if (types.some(t => (t.name || '').toLowerCase() === newName.toLowerCase())) {
      alert('Un type portant ce nom existe déjà');
      return;
    }

    try {
      setCreating(true);
      await enterpriseAdminService.createEnterpriseReportType(enterpriseId, {
        name: newName,
        description: newDescription,
        enterpriseExampleProportion: exampleProportion / 100,
        customEnterpriseInstructions: newInstructions,
        isJson: false,
        isFavorite: true   // New types are active by default
      });
      await loadTypes();
      cancelCreate();
      showNotification(`Modèle "${newName}" créé avec succès`, 'success');
    } catch (err) {
      log.error('Erreur création modèle organisation', err);
      showNotification('Erreur lors de la création du modèle', 'error');
    } finally {
      setCreating(false);
    }
  };

  const loadExamplesFor = async (reportTypeName) => {
    try {
      setExamplesLoading(true);
      const examples = await enterpriseAdminService.getEnterpriseExamples(enterpriseId, reportTypeName);
      setEditingExamples(examples);
    } finally {
      setExamplesLoading(false);
    }
  };

  const openEdit = async (rt) => {
    setEditing(rt);
    setEditedProportion(rt.enterpriseExampleProportion !== undefined ? Math.round(rt.enterpriseExampleProportion * 100) : 75);
    setEditedIsFavorite(rt.isFavorite);
    setCustomInstructionsDraft(rt.customEnterpriseInstructions || '');
    await loadExamplesFor(rt.name);
  };

  const cancelEdit = () => {
    setEditing(null);
    setEditedProportion(75);
    setEditedIsFavorite(false);
    setEditingExamples([]);
    setNewExampleTranscription('');
    setNewExampleContent('');
    setCustomInstructionsDraft('');
    setEditingExampleId(null);
  };

  const saveEdit = async () => {
    if (!editing) return;
    setSavingEdit(true);
    try {
      const promises = [];

      if ((editing.enterpriseExampleProportion !== undefined ? Math.round(editing.enterpriseExampleProportion * 100) : 75) !== editedProportion) {
        promises.push(
          enterpriseAdminService.updateEnterpriseReportType(enterpriseId, editing.id, {
            enterpriseExampleProportion: editedProportion / 100
          })
        );
      }

      if ((editing.customEnterpriseInstructions || '') !== (customInstructionsDraft || '')) {
        promises.push(
          enterpriseAdminService.updateEnterpriseReportType(enterpriseId, editing.id, {
            customEnterpriseInstructions: customInstructionsDraft || ''
          })
        );
      }

      const currentlyFavorite = !!editing.isFavorite;
      if (currentlyFavorite !== editedIsFavorite) {
        promises.push(
          enterpriseAdminService.updateEnterpriseReportType(enterpriseId, editing.id, {
            isFavorite: editedIsFavorite
          })
        );
      }

      await Promise.all(promises);
      await loadTypes();
      cancelEdit();
      showNotification(`Modèle "${editing.name}" mis à jour avec succès`, 'success');
    } catch (e) {
      log.error('Erreur mise à jour modèle organisation', e);
      showNotification('Erreur lors de la mise à jour', 'error');
    } finally {
      setSavingEdit(false);
    }
  };

  const deleteReportType = async (type) => {
    if (!window.confirm(`Supprimer le modèle "${type.name}" ?\n\nCette action supprimera également tous les exemples associés.`)) {
      return;
    }
    
    try {
      setLoading(true);
      await enterpriseAdminService.deleteEnterpriseReportType(enterpriseId, type.id);
      await loadTypes();
      showNotification(`Modèle "${type.name}" supprimé avec succès`, 'success');
    } catch (err) {
      log.error('Error deleting report modèle:', err);
      showNotification('Erreur lors de la suppression', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Example management functions
  const addExample = async () => {
    if (!editing) return;
    if (!newExampleContent.trim()) {
      alert('Le contenu corrigé est requis');
      return;
    }
    try {
      setExamplesLoading(true);
      await enterpriseAdminService.createEnterpriseExample(enterpriseId, {
        reportType: editing.name,
        correctedContent: newExampleContent,
        transcription: newExampleTranscription || undefined
      });
      setNewExampleTranscription('');
      setNewExampleContent('');
      await loadExamplesFor(editing.name);
      showNotification('Exemple ajouté avec succès', 'success');
    } finally {
      setExamplesLoading(false);
    }
  };

  const loadExampleDraft = (ex) => {
    setNewExampleTranscription(ex.transcription || '');
    setNewExampleContent(ex.correctedContent || '');
    setEditingExampleId(ex.id);
  };

  const clearExampleDraft = () => {
    setNewExampleTranscription('');
    setNewExampleContent('');
    setEditingExampleId(null);
  };

  const saveExampleDraft = async () => {
    if (!editing || !editingExampleId) return;
    if (!newExampleContent.trim()) {
      alert('Le contenu corrigé est requis');
      return;
    }
    try {
      setSavingExample(true);
      await enterpriseAdminService.updateEnterpriseExample(enterpriseId, editingExampleId, {
        transcription: newExampleTranscription || '',
        correctedContent: newExampleContent
      });
      await loadExamplesFor(editing.name);
      setEditingExampleId(null);
      showNotification('Exemple mis à jour avec succès', 'success');
    } catch (e) {
      log.error('Erreur mise à jour exemple', e);
      showNotification('Erreur lors de la mise à jour de l\'exemple', 'error');
    } finally {
      setSavingExample(false);
    }
  };

  const deleteExample = async (exampleId) => {
    if (!editing) return;
    if (!window.confirm('Supprimer cet exemple ?')) return;
    try {
      setExamplesLoading(true);
      await enterpriseAdminService.deleteEnterpriseExample(enterpriseId, exampleId);
      await loadExamplesFor(editing.name);
      showNotification('Exemple supprimé avec succès', 'success');
    } finally {
      setExamplesLoading(false);
    }
  };

  return (
    <div className="-reporttype-enteprise-enterprise-report-types-tab">
      {notification && (
        <Notification 
          message={notification.message} 
          type={notification.type} 
          show={true} 
          onClose={hideNotification} 
        />
      )}
      <div className="-reporttype-enteprise-report-types-header">
        <div className="-reporttype-enteprise-header-left">
          <h2>Modèles</h2>
          <span className="-reporttype-enteprise-help-text">
            Cliquez sur un modèle pour le modifier
          </span>
        </div>
        <div className="-reporttype-enteprise-header-actions">
          <Button onClick={openCreate} disabled={loading} icon={FaPlus} variant="primary">
            Créer
          </Button>
          {hasChanges && (
            <>
              <Button variant="secondary" onClick={handleCancelChanges} disabled={savingEdit}>
                Annuler
              </Button>
              <Button variant="primary" onClick={handleSaveChanges} disabled={savingEdit}>
                {savingEdit ? 'Sauvegarde...' : 'Sauvegarder'}
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="-reporttype-enteprise-report-types-content">
        {/* Active Types Section */}
        <div className="-reporttype-enteprise-types-section -reporttype-enteprise-active-section">
          <h3>
            <FaCheck className="-reporttype-enteprise-section-icon" /> 
            Actifs ({activeTypes.length})
            <span style={{ fontSize: '0.8em', marginLeft: 8, color: '#666', fontWeight: 400 }}>
              utilisables par les utilisateurs
            </span>
          </h3>
          <div className="-reporttype-enteprise-types-list">
            {activeTypes.length === 0 ? (
              <div className="-reporttype-enteprise-empty-state">
                <p>Aucun modèle actif</p>
                <p className="-reporttype-enteprise-hint">Activez des modèles depuis la section ci-dessous</p>
              </div>
            ) : (
              activeTypes.map((type) => (
                <div
                  key={type.id}
                  className="-reporttype-enteprise-type-item -reporttype-enteprise-active-type"
                  onClick={() => openEdit(type)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="-reporttype-enteprise-type-info">
                    <div className="-reporttype-enteprise-type-name">{formatDisplayName(type.name)}</div>
                    {type.description && (
                      <div className="-reporttype-enteprise-type-description" style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>{type.description}</div>
                    )}
                  </div>
                  <div className="-reporttype-enteprise-type-actions">
                    {type.customEnterpriseInstructions && (
                      <span 
                        className="-reporttype-enteprise-has-instructions" 
                        title="Instructions personnalisées"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <FaPencilAlt />
                      </span>
                    )}
                    <button
                      className="-reporttype-enteprise-toggle-btn -reporttype-enteprise-active"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleFavorite(type);
                      }}
                      title="Désactiver"
                    >
                      <FaCheck />
                    </button>
                    <button
                      className="-reporttype-enteprise-delete-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteReportType(type);
                      }}
                      title="Supprimer"
                    >
                      <FaTrash />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Inactive Types Section */}
        <div className="-reporttype-enteprise-types-section -reporttype-enteprise-inactive-section">
          <h3>
            Existant ({inactiveTypes.length})
            <span style={{ fontSize: '0.8em', marginLeft: 8, color: '#666', fontWeight: 400 }}>
            non visibles et non utilisables par les utilisateurs
            </span>
          </h3>
          <div className="-reporttype-enteprise-types-list">
            {inactiveTypes.length === 0 ? (
              <div className="-reporttype-enteprise-empty-state">
                <p>Tous les modèles sont actifs</p>
              </div>
            ) : (
              inactiveTypes.map((type) => (
                <div 
                  key={type.id} 
                  className="-reporttype-enteprise-type-item -reporttype-enteprise-inactive-type"
                  onClick={() => openEdit(type)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="-reporttype-enteprise-type-info">
                    <div className="-reporttype-enteprise-type-name">{formatDisplayName(type.name)}</div>
                    {type.description && (
                      <div className="-reporttype-enteprise-type-description" style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>{type.description}</div>
                    )}
                  </div>
                  <div className="-reporttype-enteprise-type-actions">
                    {type.customEnterpriseInstructions && (
                      <span 
                        className="-reporttype-enteprise-has-instructions" 
                        title="Instructions personnalisées"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <FaPencilAlt />
                      </span>
                    )}
                    <button
                      className="-reporttype-enteprise-toggle-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleFavorite(type);
                      }}
                      title="Activer"
                    >
                      <FaCheck />
                    </button>
                    <button
                      className="-reporttype-enteprise-delete-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteReportType(type);
                      }}
                      title="Supprimer"
                    >
                      <FaTrash />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Create Report Type Wizard */}
      {isCreating && (
        <CreateEnterpriseReportTypeWizard
          isOpen={isCreating}
          onClose={cancelCreate}
          step={createStep}
          setStep={setCreateStep}
          newName={newName}
          setNewName={setNewName}
          newCategory={newCategory}
          setNewCategory={setNewCategory}
          newDescription={newDescription}
          setNewDescription={setNewDescription}
          newInstructions={newInstructions}
          setNewInstructions={setNewInstructions}
          exampleProportion={exampleProportion}
          setExampleProportion={setExampleProportion}
          creating={creating}
          validateName={validateName}
          enterpriseShortName={enterpriseShortName}
          onCreate={saveCreate}
          nameTouched={nameTouched}
          setNameTouched={setNameTouched}
          existingNames={types.map(t => t.name)}
        />
      )}

      {/* Full Screen Editor for create wizard instructions */}
      <FullScreenTextEditor
        isOpen={isCreating && showInstructionEditor}
        onClose={() => setShowInstructionEditor(false)}
        onSave={async (value) => { setNewInstructions(value); }}
        initialValue={newInstructions}
        title={'Instructions personnalisées'}
        placeholder="Entrez les instructions personnalisées..."
        saveButtonText="Utiliser ce texte"
      />

      {/* Edit Report Type Modal */}
      {editing && (
        <>
          <div className="-reporttype-enteprise-modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) cancelEdit(); }}>
            <div className="-reporttype-enteprise-modal" style={{ maxWidth: 680 }} onMouseDown={(e) => e.stopPropagation()}>
              <h3>Modifier {formatDisplayName(editing.name)}</h3>
              
              {/* Status Section */}
              <div className="-reporttype-enteprise-modal-form-section">
                <label>
                  <span className="-reporttype-enteprise-modal-label-text">
                    Statut du modèle
                  </span>
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                  <Toggle checked={editedIsFavorite} onChange={setEditedIsFavorite} label={editedIsFavorite ? "Actif" : "Inactif"} />
                  <div className="-reporttype-enteprise-modal-helper-text" style={{ margin: 0 }}>
                    {editedIsFavorite ? 
                      "Ce modèle est visible pour tous les utilisateurs" : 
                      "Ce modèle est désactivé et non visible"
                    }
                  </div>
                </div>
              </div>

                      {/* Examples Management Section */}
                      <div className="-reporttype-enteprise-modal-form-section">
                <label>
                  <span className="-reporttype-enteprise-modal-label-text">
                    Gestion des exemples
                  </span>
                </label>
               <Button variant="primary" icon={FaPlus} onClick={() => setShowExamplesManager(true)}>
                  Ajouter des exemples
                </Button>
                <div className="-reporttype-enteprise-modal-helper-text">
                  Ajoutez et gérez les exemples pour améliorer la génération de rapports
                </div>
              </div>

              {/* Proportion Options Section */}
              <div className="-reporttype-enteprise-modal-slider-section">
                <div className="-reporttype-enteprise-modal-slider-header">
                  <label style={{ margin: 0 }}>
                    <span className="-reporttype-enteprise-modal-label-text">
                      Restrictions des utilisateurs
                    </span>
                  </label>
                  <span className="-reporttype-enteprise-modal-slider-value">
                    {editedProportion}%
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  {[25, 50, 75, 100].map(value => (
                    <button
                      key={value}
                      onClick={() => setEditedProportion(value)}
                      style={{
                        flex: 1,
                        padding: '12px 16px',
                        border: `2px solid ${editedProportion === value ? '#960018' : '#e0e0e0'}`,
                        borderRadius: 8,
                        background: editedProportion === value ? '#960018' : 'white',
                        color: editedProportion === value ? 'white' : '#333',
                        fontWeight: editedProportion === value ? 600 : 400,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        fontSize: 14
                      }}
                    >
                      {value}%
                    </button>
                  ))}
                </div>
                <div className="-reporttype-enteprise-modal-helper-text" style={{ 
                  marginTop: 12,
                  padding: '12px',
                  borderRadius: '8px',
                  background: `linear-gradient(135deg, 
                    rgba(59, 130, 246, ${0.1 + (1 - editedProportion/100) * 0.15}), 
                    rgba(220, 38, 38, ${0.1 + (editedProportion/100) * 0.15}))`,
                  border: `1px solid rgba(${editedProportion < 50 ? '59, 130, 246' : '220, 38, 38'}, 0.3)`
                }}>
                  {editedProportion === 25 && (
                    <><strong style={{color: '#3b82f6'}}>Libre (25%) :</strong> L'utilisateur dispose d'une grande liberté, avec une légère orientation par les consignes de l'organisation.</>
                  )}
                  {editedProportion === 50 && (
                    <><strong style={{color: '#ec4899'}}>Équilibré (50%) :</strong> Équilibre entre la personnalisation individuelle et les standards de l'organisation.</>
                  )}
                  {editedProportion === 75 && (
                    <><strong style={{color: '#dc2626'}}>Structuré (75%) :</strong> Les standards de l'organisation sont fortement privilégiés, tout en laissant une marge d'adaptation. <em>Recommandé.</em></>
                  )}
                  {editedProportion === 100 && (
                    <><strong style={{color: '#991b1b'}}>Rigide (100%) :</strong> Les contenus sont strictement conformes aux attentes de l'organisation, sans personnalisation possible.</>
                  )}
                </div>
              </div>

              {/* Instructions Section */}
              <div className="-reporttype-enteprise-modal-form-section">
                <label>
                  <span className="-reporttype-enteprise-modal-label-text">
                    Instructions personnalisées
                  </span>
                </label>
                <div
                  role="button"
                  onClick={() => setShowInstructionEditor(true)}
                  tabIndex={0}
                  style={{
                    padding: 14,
                    border: '2px solid #e0e0e0',
                    borderRadius: 10,
                    background: 'linear-gradient(135deg, #fafbfc, #f8f9fa)',
                    color: '#333',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    minHeight: 60,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#960018';
                    e.currentTarget.style.background = 'white';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#e0e0e0';
                    e.currentTarget.style.background = 'linear-gradient(135deg, #fafbfc, #f8f9fa)';
                  }}
                  title={customInstructionsDraft || editing?.customEnterpriseInstructions || 'Cliquez pour éditer'}
                >
                  <div style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {customInstructionsDraft || editing?.customEnterpriseInstructions || 
                      <span style={{ color: '#999', fontStyle: 'italic' }}>Cliquez pour ajouter des instructions...</span>
                    }
                  </div>
                  <FaPencilAlt style={{ color: '#960018', flexShrink: 0 }} />
                </div>
                <div className="-reporttype-enteprise-modal-helper-text">
                  Instructions appliquées à ce type pour tous les utilisateurs de l'organisation
                </div>
              </div>

    
              <div className="-reporttype-enteprise-modal-actions">
                <Button icon={FaCheck} onClick={saveEdit} disabled={savingEdit} variant="primary">
                  {savingEdit ? 'Enregistrement...' : 'Enregistrer les modifications'}
                </Button>
                <Button icon={FaTimes} onClick={cancelEdit} disabled={savingEdit} variant="secondary">
                  Annuler
                </Button>
              </div>
            </div>
          </div>

          {/* Examples Manager Modal */}
          {editing && showExamplesManager && (
            <div className="-reporttype-enteprise-modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) setShowExamplesManager(false); }}>
              <div
                className="-reporttype-enteprise-modal"
                style={{ maxWidth: 1200, width: '90vw', height: '85vh' }}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h3>Exemples – {formatDisplayName(editing.name)}</h3>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      type="text"
                      placeholder="Rechercher..."
                      value={searchExamples}
                      onChange={(e) => setSearchExamples(e.target.value)}
                      style={{ padding: 8, borderRadius: 6, border: '1px solid #ddd', minWidth: 220 }}
                    />
                    <Button variant="secondary" icon={FaTimes} onClick={() => setShowExamplesManager(false)}>Fermer</Button>
                  </div>
                </div>

                <div style={{ marginTop: 12, maxHeight: '70vh', overflowY: 'auto' }}>
                  {examplesLoading ? (
                    <p>Chargement…</p>
                  ) : (
                    <div style={{ display: 'grid', gap: 10 }}>
                      {(editingExamples || [])
                        .filter(ex => {
                          if (!searchExamples.trim()) return true;
                          const q = searchExamples.toLowerCase();
                          return (
                            (ex.transcription || '').toLowerCase().includes(q) ||
                            (ex.correctedContent || '').toLowerCase().includes(q)
                          );
                        })
                        .map((ex) => (
                          <div key={ex.id} style={{ border: '1px solid #eee', borderRadius: 10, overflow: 'hidden', background: '#fff' }}>
                            <div
                              role="button"
                              tabIndex={0}
                              onClick={() => setExpandedExampleId(prev => (prev === ex.id ? null : ex.id))}
                              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpandedExampleId(prev => (prev === ex.id ? null : ex.id)); } }}
                              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 12, cursor: 'pointer', background: '#fafafa' }}
                            >
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <strong style={{ fontSize: 14 }}>Exemple #{ex.id.slice(0, 8)}</strong>
                                <span style={{ fontSize: 12, color: '#777' }}>
                                  {ex.transcription ? 'Avec transcription' : 'Sans transcription'}
                                </span>
                              </div>
                              <div style={{ display: 'flex', gap: 8 }}>
                                <Button size="small" variant="secondary" icon={FaPencilAlt} onClick={(e) => { e.stopPropagation(); setExpandedExampleId(ex.id); loadExampleDraft(ex); }}>
                                  Modifier
                                </Button>
                                <Button size="small" variant="danger" icon={FaTrash} onClick={(e) => { e.stopPropagation(); deleteExample(ex.id); }}>
                                  Supprimer
                                </Button>
                              </div>
                            </div>

                            {expandedExampleId === ex.id && (
                              <div style={{ padding: 12, borderTop: '1px solid #eee', background: '#fff' }}>
                                {editingExampleId === ex.id ? (
                                  <>
                                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                                      <div style={{ flex: 1, minWidth: 260 }}>
                                        <label style={{ fontWeight: 600, fontSize: 12, color: '#555' }}>Transcription</label>
                                        <div
                                          role="button"
                                          onClick={() => setShowTranscriptionEditor(true)}
                                          onFocus={() => setShowTranscriptionEditor(true)}
                                          tabIndex={0}
                                          style={{
                                            minHeight: 60,
                                            padding: 10,
                                            border: '1px solid #e0e0e0',
                                            borderRadius: 8,
                                            background: '#fafafa',
                                            color: '#333',
                                            whiteSpace: 'pre-wrap',
                                            overflow: 'hidden',
                                            cursor: 'text'
                                          }}
                                          title={newExampleTranscription || ''}
                                        >
                                          {newExampleTranscription || 'Cliquer pour saisir la transcription…'}
                                        </div>
                                      </div>
                                      <div style={{ flex: 1, minWidth: 260 }}>
                                        <label style={{ fontWeight: 600, fontSize: 12, color: '#555' }}>Contenu corrigé</label>
                                        <div
                                          role="button"
                                          onClick={() => setShowExampleEditor(true)}
                                          onFocus={() => setShowExampleEditor(true)}
                                          tabIndex={0}
                                          style={{
                                            minHeight: 80,
                                            padding: 10,
                                            border: '1px solid #e0e0e0',
                                            borderRadius: 8,
                                            background: '#fafafa',
                                            color: '#333',
                                            whiteSpace: 'pre-wrap',
                                            overflow: 'hidden',
                                            cursor: 'text'
                                          }}
                                          title={newExampleContent || ''}
                                        >
                                          {newExampleContent || 'Cliquer pour saisir le contenu corrigé…'}
                                        </div>
                                      </div>
                                    </div>
                                    <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                                      <Button variant="secondary" icon={FaTimes} onClick={() => { clearExampleDraft(); }} disabled={examplesLoading}>Annuler</Button>
                                      <Button variant="primary" icon={FaCheck} onClick={saveExampleDraft} disabled={savingExample || examplesLoading || !newExampleContent.trim()}>
                                        {savingExample ? 'Enregistrement…' : 'Enregistrer les modifications'}
                                      </Button>
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    {ex.transcription && (
                                      <div style={{ marginBottom: 12 }}>
                                        <label style={{ fontWeight: 600, fontSize: 12, color: '#555', display: 'block', marginBottom: 6 }}>Transcription</label>
                                        <div style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', background: '#f9fafb', padding: 10, borderRadius: 8, border: '1px solid #f0f0f0', fontFamily: 'inherit', fontSize: 'inherit', lineHeight: 'inherit' }}>{ex.transcription}</div>
                                      </div>
                                    )}
                                    <div>
                                      <label style={{ fontWeight: 600, fontSize: 12, color: '#555', display: 'block', marginBottom: 6 }}>Contenu corrigé</label>
                                      <div style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', background: '#f9fafb', padding: 10, borderRadius: 8, border: '1px solid #f0f0f0', fontFamily: 'inherit', fontSize: 'inherit', lineHeight: 'inherit' }}>{ex.correctedContent || ''}</div>
                                    </div>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        ))}

                      {expandedExampleId === 'new' && (
                        <div style={{ border: '1px solid #eee', borderRadius: 10, overflow: 'hidden', background: '#fff' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 12, background: '#fafafa' }}>
                            <strong>Nouveau exemple</strong>
                            <div style={{ display: 'flex', gap: 8 }}>
                              <Button variant="secondary" icon={FaTimes} onClick={() => { setExpandedExampleId(null); clearExampleDraft(); }}>Annuler</Button>
                            </div>
                          </div>
                          <div style={{ padding: 12 }}>
                            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                              <div style={{ flex: 1, minWidth: 260 }}>
                                <label style={{ fontWeight: 600, fontSize: 12, color: '#555' }}>Transcription</label>
                                <div
                                  role="button"
                                  onClick={() => setShowTranscriptionEditor(true)}
                                  onFocus={() => setShowTranscriptionEditor(true)}
                                  tabIndex={0}
                                  style={{
                                    minHeight: 60,
                                    padding: 10,
                                    border: '1px solid #e0e0e0',
                                    borderRadius: 8,
                                    background: '#fafafa',
                                    color: '#333',
                                    whiteSpace: 'pre-wrap',
                                    overflow: 'hidden',
                                    cursor: 'text'
                                  }}
                                  title={newExampleTranscription || ''}
                                >
                                  {newExampleTranscription || 'Cliquer pour saisir la transcription…'}
                                </div>
                              </div>
                              <div style={{ flex: 1, minWidth: 260 }}>
                                <label style={{ fontWeight: 600, fontSize: 12, color: '#555' }}>Contenu corrigé</label>
                                <div
                                  role="button"
                                  onClick={() => setShowExampleEditor(true)}
                                  onFocus={() => setShowExampleEditor(true)}
                                  tabIndex={0}
                                  style={{
                                    minHeight: 80,
                                    padding: 10,
                                    border: '1px solid #e0e0e0',
                                    borderRadius: 8,
                                    background: '#fafafa',
                                    color: '#333',
                                    whiteSpace: 'pre-wrap',
                                    overflow: 'hidden',
                                    cursor: 'text'
                                  }}
                                  title={newExampleContent || ''}
                                >
                                  {newExampleContent || 'Cliquer pour saisir le contenu corrigé…'}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {(!editingExamples || editingExamples.length === 0) && (
                        <p style={{ margin: 0, color: '#777' }}>Aucun exemple</p>
                      )}
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', justifyContent: 'center', marginTop: 10 }}>
                  <Button
                    variant={expandedExampleId === 'new' ? 'success' : 'primary'}
                    icon={expandedExampleId === 'new' ? FaCheck : FaPlus}
                    onClick={() => {
                      if (expandedExampleId === 'new') {
                        addExample();
                      } else {
                        setExpandedExampleId('new');
                        clearExampleDraft();
                      }
                    }}
                    disabled={expandedExampleId === 'new' ? (examplesLoading || !newExampleContent.trim()) : false}
                  >
                    {expandedExampleId === 'new' ? 'Valider' : 'Ajouter un exemple'}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Full Screen Editors */}
          <FullScreenTextEditor
            isOpen={showInstructionEditor}
            onClose={() => setShowInstructionEditor(false)}
            onSave={async (value) => { setCustomInstructionsDraft(value); }}
            initialValue={customInstructionsDraft}
            title={editing ? `Instructions pour ${formatDisplayName(editing.name)}` : 'Instructions personnalisées'}
            placeholder="Entrez les instructions personnalisées..."
            saveButtonText="Utiliser ce texte"
          />
          <FullScreenTextEditor
            isOpen={showExampleEditor}
            onClose={() => setShowExampleEditor(false)}
            onSave={async (value) => { setNewExampleContent(value); }}
            initialValue={newExampleContent}
            title={editing ? `Exemple - contenu corrigé pour ${formatDisplayName(editing.name)}` : 'Contenu corrigé'}
            placeholder="Entrez le contenu corrigé..."
            saveButtonText="Utiliser ce texte"
          />
          <FullScreenTextEditor
            isOpen={showTranscriptionEditor}
            onClose={() => setShowTranscriptionEditor(false)}
            onSave={async (value) => { setNewExampleTranscription(value); }}
            initialValue={newExampleTranscription}
            title={editing ? `Exemple - transcription pour ${editing.name}` : 'Transcription'}
            placeholder="Entrez la transcription..."
            saveButtonText="Utiliser ce texte"
          />
        </>
      )}
    </div>
  );
}

export default ReportTypesTab;